/* eslint-disable max-lines */
import { editLanguageMatcher, receiveNewKeyword } from 'actions/identifier';
import ToggleButton from 'components/ToggleButton';
import WarningMsg from 'components/WarningMsg';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  getActiveLanguageMatcher,
  getActiveLanguageMatcherId,
  getCompareMode,
  getShouldDisableEdit,
  saveIdentifierRequestLoading,
  unsavedChanges,
} from 'selectors/identifier';
import { useSelector } from 'store';
import { languageMatcherNotificationMsgs } from 'utils/userNotificationMsgs';
import DeleteMatcherButton from '../DeleteMatcherButton';
import LanguageMatcherHeader from '../LanguageMatcherHeader';
import ListFieldWithImport from '../shared/ListFieldWithImport';

type Props = { isSaved: boolean };

const MetadataRecipientForm: React.FC<Props> = ({ isSaved }) => {
  const dispatch = useDispatch();
  const unsaved = useSelector(unsavedChanges);
  const isLoadingSaveIdentifier = useSelector(saveIdentifierRequestLoading);
  const compareMode = useSelector(getCompareMode);
  const shouldDisable = useSelector(getShouldDisableEdit);
  const activeLanguageMatcher = useSelector(getActiveLanguageMatcher);
  const activeLanguageMatcherId = useSelector(getActiveLanguageMatcherId);

  const onEditLanguageMatcher = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (!activeLanguageMatcher.uuid || e.target.value === null) return;
    dispatch(
      editLanguageMatcher({
        languageMatcherId: activeLanguageMatcher.uuid,
        data: { [e.target.name]: e.target.value },
      })
    );
  };

  const toggleExcludeFilters = (): void => {
    // @ts-ignore
    const value = !activeLanguageMatcher.filters_exclude;
    dispatch(
      receiveNewKeyword({
        languageMatcherId: activeLanguageMatcherId,
        parentType: 'metadata_recipient',
        data: { key: 'filters_exclude', value },
      })
    );
    dispatch(
      receiveNewKeyword({
        languageMatcherId: activeLanguageMatcherId,
        parentType: 'metadata_recipient',
        data: { key: 'saved', value: false },
      })
    );
  };

  return (
    <div className="flex flex-col pt-5 pl-10 pr-4 pb-6 text-body h-full">
      <div className="flex flex-row justify-end">
        <WarningMsg
          message={languageMatcherNotificationMsgs.unsavedChanges}
          showLoading={isLoadingSaveIdentifier}
          showWarning={unsaved}
          showSavedMsg={isSaved}
        />
      </div>
      <div className="-mt-4 flex flex-col gap-4 text-body h-full">
        <LanguageMatcherHeader />

        <div className="mt-6 flex flex-col gap-4">
          <ListFieldWithImport
            label="Domains to match"
            languageMatcherId={activeLanguageMatcherId}
            parentType="metadata_recipient"
            field="domains"
            value={(activeLanguageMatcher.domains as string[]) || []}
            compareMode={compareMode}
            shouldDisable={shouldDisable}
          />

          <ListFieldWithImport
            label="Top level domains to match"
            helperText={`e.g. com, gov, edu, io. (Don't include the dot ".")`}
            languageMatcherId={activeLanguageMatcherId}
            parentType="metadata_recipient"
            field="tlds"
            value={(activeLanguageMatcher.tlds as string[]) || []}
            compareMode={compareMode}
            shouldDisable={shouldDisable}
          />

          <ListFieldWithImport
            label="Exact email address to match"
            languageMatcherId={activeLanguageMatcherId}
            parentType="metadata_recipient"
            field="emails"
            value={(activeLanguageMatcher.emails as string[]) || []}
            compareMode={compareMode}
            shouldDisable={shouldDisable}
          />

          <div className="mt-10 flex flex-row gap-4">
            <div className="flex flex-col gap-1 w-62">
              <span className="text-body">Minimum number of recipients</span>
              <input
                name="min_count"
                id="min_count"
                type="number"
                data-testid="metadata-recipient-min-count-input"
                // @ts-ignore
                value={activeLanguageMatcher.min_count ?? ''}
                onChange={onEditLanguageMatcher}
                className="form-input w-full h-9 py-2 px-4 border border-litlingo-gray-2 rounded-lg text-body focus:outline-none"
                disabled={compareMode || shouldDisable}
              />
            </div>

            <div className="flex flex-col gap-1 w-60">
              <span className="text-body">Maximum number of recipients</span>
              <input
                name="max_count"
                id="max_count"
                type="number"
                data-testid="metadata-recipient-max-count-input"
                // @ts-ignore
                value={activeLanguageMatcher.max_count ?? ''}
                onChange={onEditLanguageMatcher}
                className="form-input w-full h-9 py-2 px-4 border border-litlingo-gray-2 rounded-lg text-body focus:outline-none"
                disabled={compareMode || shouldDisable}
              />
            </div>
          </div>

          <div className="flex flex-row items-center gap-6 pt-6">
            <ToggleButton
              // @ts-ignore
              isOn={activeLanguageMatcher.filters_exclude as boolean}
              handleToggleClick={toggleExcludeFilters}
              disabled={compareMode || shouldDisable}
            />
            <span>Use filters to exclude</span>
          </div>

          <div className="mt-10">
            <ListFieldWithImport
              label="Enable for users in Team (UUIDs)"
              languageMatcherId={activeLanguageMatcherId}
              parentType="metadata_recipient"
              // @ts-ignore
              field="teams"
              // @ts-ignore
              value={(activeLanguageMatcher.teams as string[]) || []}
              compareMode={compareMode}
              shouldDisable={shouldDisable}
              enableImport={false}
            />
          </div>
        </div>

        <div className="mt-auto pt-6 flex flex-row justify-end">
          <DeleteMatcherButton
            text="Delete Matcher"
            setValue={(): void => {
              // moot
            }}
            setHighlightedLanguageMatcher={(): void => {
              // moot
            }}
            activeLanguageMatcher={activeLanguageMatcher}
          />
        </div>
      </div>
    </div>
  );
};

export default MetadataRecipientForm;
